/**
 * SupportSlaPage — operator-facing console for the
 * `24-7-tier-blind-support-with-published-sla` surface.
 *
 * The feature commits to one identical SLA for every customer plan tier,
 * so "tier-blind" means the response-time guarantees do NOT vary by plan.
 * The page shows:
 *
 *   - Published SLA card    (GET /sla)            — the four response windows.
 *   - Filter strip          (priority + status)   — client-side, no server round-trip.
 *   - Summary tiles         (open / breached)     — derived from list rows.
 *   - Tickets table         (GET /)               — status + priority + SLA badges.
 *   - New ticket form       (POST /)              — idempotent via client_ticket_id.
 *
 * Brand tokens follow the repo CLAUDE.md palette: GREEN-only accent
 * (`green-600` #16a34a, `green-50` tint), zinc-200 borders, 6-8px rounded
 * corners, Inter font, light theme only. No emerald/sky/blue/orange/purple,
 * no emojis, no competitor names. Icons are inline SVG.
 */

import { useMemo, useState, type FormEvent } from "react";
import {
	countBy,
	formatCreatedAt,
	formatSlaWindow,
	isSlaBreached,
	PRIORITY_ORDER,
	priorityLabel,
	slaDeadline,
	sortTickets,
	statusLabel,
	type CreateTicketBody,
	type TicketPriority,
	type TicketRow,
	type TicketStatus,
} from "./api";
import {
	usePublishedSla,
	useSupportTicketCreate,
	useSupportTicketList,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — GREEN-only palette per repo brand spec.
// ---------------------------------------------------------------------------

const PAGE = "mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10";
const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.06),0_4px_8px_-2px_rgb(24_24_27/0.04)]";
const LABEL = "text-sm font-medium text-zinc-700";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT = INPUT;
const TEXTAREA = `${INPUT} font-mono text-xs`;
const BTN_PRIMARY =
	"inline-flex items-center justify-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Inline SVG icons (no emoji, no competitor glyphs).
// ---------------------------------------------------------------------------

function IconShield(props: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<path d="M12 3 4.5 6v6c0 5 3.5 8.5 7.5 9 4-0.5 7.5-4 7.5-9V6Z" />
			<path d="M9 12l2 2 4-4" />
		</svg>
	);
}

function IconClock(props: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<circle cx="12" cy="12" r="9" />
			<path d="M12 7v5l3 2" />
		</svg>
	);
}

function IconAlert(props: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<path d="M12 3 2.5 20h19Z" />
			<path d="M12 10v4" />
			<circle cx="12" cy="17" r="0.75" fill="currentColor" />
		</svg>
	);
}

function IconPlus(props: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

interface Filters {
	priority: TicketPriority | "all";
	status: TicketStatus | "all";
}

const DEFAULT_FILTERS: Filters = { priority: "all", status: "all" };

const EMPTY_DRAFT: CreateTicketBody = {
	client_ticket_id: "",
	subject: "",
	body: "",
	priority: "normal",
};

export function SupportSlaPage() {
	const listQuery = useSupportTicketList();
	const slaQuery = usePublishedSla();
	const createMut = useSupportTicketCreate();

	const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
	const [draft, setDraft] = useState<CreateTicketBody>(EMPTY_DRAFT);
	const [draftError, setDraftError] = useState<string | null>(null);

	const rows = listQuery.data?.items ?? [];
	const nowSec = Math.floor(Date.now() / 1000);

	const filtered = useMemo(() => {
		const all = sortTickets(rows, nowSec);
		return all.filter((r) => {
			if (filters.priority !== "all" && r.priority !== filters.priority) return false;
			if (filters.status !== "all" && r.status !== filters.status) return false;
			return true;
		});
	}, [rows, filters.priority, filters.status, nowSec]);

	const openCount = countBy(rows, (r) => r.status !== "resolved");
	const breachedCount = countBy(rows, (r) => isSlaBreached(r, nowSec));
	const urgentCount = countBy(
		rows,
		(r) => r.priority === "urgent" && r.status !== "resolved",
	);

	function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setDraftError(null);
		if (!draft.client_ticket_id.trim()) {
			setDraftError("Ticket reference required.");
			return;
		}
		if (!draft.subject.trim()) {
			setDraftError("Subject required.");
			return;
		}
		if (!draft.body.trim()) {
			setDraftError("Body required.");
			return;
		}
		createMut.mutate(
			{
				client_ticket_id: draft.client_ticket_id.trim(),
				subject: draft.subject.trim(),
				body: draft.body.trim(),
				priority: draft.priority,
			},
			{
				onSuccess: () => setDraft(EMPTY_DRAFT),
				onError: (err) => setDraftError(err.message),
			},
		);
	}

	return (
		<main
			data-testid="24-7-tier-blind-support-with-published-sla-page"
			className={PAGE}
		>
			<Header />
			<PublishedSlaCard
				loading={slaQuery.isLoading}
				error={slaQuery.error?.message ?? null}
				data={slaQuery.data ?? null}
			/>
			<SummaryTiles
				total={rows.length}
				open={openCount}
				breached={breachedCount}
				urgent={urgentCount}
			/>
			<FilterStrip
				value={filters}
				onChange={setFilters}
				disabled={listQuery.isLoading}
			/>
			<TicketsTable
				rows={filtered}
				total={rows.length}
				loading={listQuery.isLoading}
				error={listQuery.error?.message ?? null}
				nowSec={nowSec}
			/>
			<CreateTicketCard
				draft={draft}
				onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
				submitting={createMut.isPending}
				error={draftError}
				onSubmit={handleSubmit}
			/>
		</main>
	);
}

export default SupportSlaPage;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
				Support program
			</p>
			<h1 className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
				24/7 support with a published SLA
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Every plan tier on the platform receives the same response-time
				commitment. File tickets here, track breaches, and review the
				response windows your team is held to.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Published SLA card — render the four response windows.
// ---------------------------------------------------------------------------

interface PublishedSlaCardProps {
	loading: boolean;
	error: string | null;
	data:
		| {
				tier_blind: boolean;
				response_seconds: Record<TicketPriority, number>;
				note: string;
		  }
		| null;
}

function PublishedSlaCard(props: PublishedSlaCardProps) {
	const rows = PRIORITY_ORDER.map((p) => ({
		priority: p,
		seconds: props.data?.response_seconds?.[p],
	}));

	return (
		<section
			aria-label="Published response-time windows"
			data-testid="sla-card"
			className={`${CARD} p-5`}
		>
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-[6px] border border-green-600 bg-green-50 text-green-700">
						<IconShield className="h-4 w-4" />
					</span>
					<div className="flex flex-col gap-1">
						<h2 className="text-base font-semibold text-zinc-900">
							Published SLA
						</h2>
						<p className={HINT}>
							Identical response windows for every plan tier. Tier-blind by design.
						</p>
					</div>
				</div>
				<span
					className="inline-flex items-center gap-1 rounded-[6px] border border-green-600 bg-green-50 px-2 py-1 text-xs font-medium text-green-800"
					data-testid="sla-tier-blind-badge"
				>
					<IconShield className="h-3 w-3" />
					Tier-blind
				</span>
			</div>
			<div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
				{rows.map((r) => (
					<div
						key={r.priority}
						className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-3"
						data-testid={`sla-tile-${r.priority}`}
					>
						<p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
							{priorityLabel(r.priority)}
						</p>
						<p className="mt-1 font-mono text-lg text-zinc-900">
							{props.loading
								? "…"
								: typeof r.seconds === "number"
									? formatSlaWindow(r.seconds)
									: "—"}
						</p>
					</div>
				))}
			</div>
			{props.error ? (
				<p className={`mt-3 ${ERROR_TEXT}`} role="alert">
					{props.error}
				</p>
			) : null}
			{props.data?.note ? (
				<p className="mt-3 text-xs text-zinc-600">{props.data.note}</p>
			) : null}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Summary tiles
// ---------------------------------------------------------------------------

function SummaryTiles(props: {
	total: number;
	open: number;
	breached: number;
	urgent: number;
}) {
	return (
		<section
			aria-label="Ticket summary"
			data-testid="support-summary"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
		>
			<Tile label="Total" value={props.total} tone="neutral" />
			<Tile label="Open" value={props.open} tone="green" />
			<Tile label="Urgent open" value={props.urgent} tone="neutral" />
			<Tile
				label="SLA breached"
				value={props.breached}
				tone={props.breached > 0 ? "red" : "neutral"}
			/>
		</section>
	);
}

function Tile(props: {
	label: string;
	value: number;
	tone: "neutral" | "green" | "red";
}) {
	const tone =
		props.tone === "green"
			? "border-green-600 bg-green-50 text-green-800"
			: props.tone === "red"
				? "border-red-200 bg-red-50 text-red-700"
				: "border-zinc-200 bg-white text-zinc-900";
	return (
		<div
			className={`${CARD} flex items-center justify-between px-4 py-3`}
			data-testid={`tile-${props.label.toLowerCase().replace(/\s+/g, "-")}`}
		>
			<p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
				{props.label}
			</p>
			<span
				className={`inline-flex min-w-[2.5rem] items-center justify-center rounded-[6px] border px-2 py-0.5 font-mono text-sm ${tone}`}
			>
				{props.value}
			</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Filter strip
// ---------------------------------------------------------------------------

function FilterStrip(props: {
	value: Filters;
	onChange: (next: Filters) => void;
	disabled: boolean;
}) {
	return (
		<section
			aria-label="Filter tickets"
			data-testid="filter-strip"
			className={`${CARD} flex flex-wrap items-end gap-3 p-4`}
		>
			<div className="flex flex-col gap-1">
				<label htmlFor="f-priority" className={LABEL}>
					Priority
				</label>
				<select
					id="f-priority"
					data-testid="filter-priority"
					className={SELECT}
					value={props.value.priority}
					disabled={props.disabled}
					onChange={(e) =>
						props.onChange({
							...props.value,
							priority: e.currentTarget.value as Filters["priority"],
						})
					}
				>
					<option value="all">All priorities</option>
					{PRIORITY_ORDER.map((p) => (
						<option key={p} value={p}>
							{priorityLabel(p)}
						</option>
					))}
				</select>
			</div>
			<div className="flex flex-col gap-1">
				<label htmlFor="f-status" className={LABEL}>
					Status
				</label>
				<select
					id="f-status"
					data-testid="filter-status"
					className={SELECT}
					value={props.value.status}
					disabled={props.disabled}
					onChange={(e) =>
						props.onChange({
							...props.value,
							status: e.currentTarget.value as Filters["status"],
						})
					}
				>
					<option value="all">All statuses</option>
					<option value="open">Open</option>
					<option value="ack">Acknowledged</option>
					<option value="resolved">Resolved</option>
				</select>
			</div>
			<button
				type="button"
				className={BTN_SECONDARY}
				data-testid="filter-reset"
				disabled={props.disabled}
				onClick={() => props.onChange(DEFAULT_FILTERS)}
			>
				Reset
			</button>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Tickets table
// ---------------------------------------------------------------------------

function TicketsTable(props: {
	rows: TicketRow[];
	total: number;
	loading: boolean;
	error: string | null;
	nowSec: number;
}) {
	return (
		<section
			aria-label="Tickets"
			data-testid="tickets-section"
			className={`${CARD} flex flex-col gap-3 p-5`}
		>
			<div className="flex items-center justify-between">
				<h2 className="text-base font-semibold text-zinc-900">Tickets</h2>
				<span className={HINT}>
					{props.rows.length} shown / {props.total} total
				</span>
			</div>
			{props.loading ? (
				<p
					className="text-sm text-zinc-500"
					data-testid="24-7-tier-blind-support-with-published-sla-loading"
				>
					Loading tickets…
				</p>
			) : props.error ? (
				<p className={ERROR_TEXT} role="alert">
					{props.error}
				</p>
			) : props.rows.length === 0 ? (
				<p
					className="text-sm text-zinc-500"
					data-testid="24-7-tier-blind-support-with-published-sla-empty"
				>
					{props.total === 0
						? "No tickets yet. Create one below to exercise the SLA clock."
						: "No tickets match the current filters."}
				</p>
			) : (
				<div className="overflow-x-auto rounded-[6px] border border-zinc-200">
					<table
						className="min-w-full divide-y divide-zinc-200 text-sm"
						data-testid="tickets-table"
					>
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-[0.1em] text-zinc-500">
							<tr>
								<th scope="col" className="px-3 py-2">Ticket</th>
								<th scope="col" className="px-3 py-2">Subject</th>
								<th scope="col" className="px-3 py-2">Priority</th>
								<th scope="col" className="px-3 py-2">Status</th>
								<th scope="col" className="px-3 py-2">SLA</th>
								<th scope="col" className="px-3 py-2">Created</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{props.rows.map((row) => {
								const breached = isSlaBreached(row, props.nowSec);
								return (
									<tr
										key={row.id}
										data-testid={`ticket-row-${row.id}`}
										className="align-top"
									>
										<td className="px-3 py-2 font-mono text-xs text-zinc-700">
											{row.client_ticket_id}
										</td>
										<td className="px-3 py-2 text-zinc-900">{row.subject}</td>
										<td className="px-3 py-2">
											<PriorityBadge priority={row.priority} />
										</td>
										<td className="px-3 py-2">
											<StatusBadge status={row.status} />
										</td>
										<td className="px-3 py-2">
											<SlaBadge
												targetSeconds={row.sla_target_seconds}
												deadline={slaDeadline(row)}
												breached={breached}
												resolved={row.status === "resolved"}
											/>
										</td>
										<td className="px-3 py-2 text-xs text-zinc-500">
											{formatCreatedAt(row.created_at, props.nowSec)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function PriorityBadge(props: { priority: TicketPriority }) {
	const map: Record<TicketPriority, string> = {
		urgent:
			"border-red-300 bg-red-50 text-red-700",
		high:
			"border-zinc-300 bg-zinc-50 text-zinc-800",
		normal:
			"border-zinc-200 bg-white text-zinc-700",
		low:
			"border-zinc-200 bg-zinc-50 text-zinc-500",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[4px] border px-1.5 py-0.5 text-[11px] font-medium ${map[props.priority]}`}
			data-testid={`priority-badge-${props.priority}`}
		>
			{priorityLabel(props.priority)}
		</span>
	);
}

function StatusBadge(props: { status: TicketStatus }) {
	const map: Record<TicketStatus, string> = {
		open:
			"border-green-600 bg-green-50 text-green-800",
		ack:
			"border-zinc-300 bg-white text-zinc-700",
		resolved:
			"border-zinc-200 bg-zinc-50 text-zinc-500",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[4px] border px-1.5 py-0.5 text-[11px] font-medium ${map[props.status]}`}
			data-testid={`status-badge-${props.status}`}
		>
			{statusLabel(props.status)}
		</span>
	);
}

function SlaBadge(props: {
	targetSeconds: number;
	deadline: number;
	breached: boolean;
	resolved: boolean;
}) {
	const window = formatSlaWindow(props.targetSeconds);
	if (props.resolved) {
		return (
			<span className="inline-flex items-center gap-1 rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[11px] font-medium text-zinc-600">
				<IconClock className="h-3 w-3" />
				{window}
			</span>
		);
	}
	if (props.breached) {
		return (
			<span
				className="inline-flex items-center gap-1 rounded-[4px] border border-red-300 bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700"
				data-testid="sla-breached"
			>
				<IconAlert className="h-3 w-3" />
				Breached · {window}
			</span>
		);
	}
	return (
		<span
			className="inline-flex items-center gap-1 rounded-[4px] border border-green-600 bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-800"
			data-testid="sla-on-track"
		>
			<IconClock className="h-3 w-3" />
			{window}
		</span>
	);
}

// ---------------------------------------------------------------------------
// Create-ticket card
// ---------------------------------------------------------------------------

function CreateTicketCard(props: {
	draft: CreateTicketBody;
	onChange: (patch: Partial<CreateTicketBody>) => void;
	submitting: boolean;
	error: string | null;
	onSubmit: (e: FormEvent) => void;
}) {
	return (
		<section
			aria-label="New ticket"
			data-testid="create-ticket-card"
			className={`${CARD} p-5`}
		>
			<div className="flex items-start gap-3">
				<span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-[6px] border border-zinc-200 bg-white text-zinc-600">
					<IconPlus className="h-4 w-4" />
				</span>
				<div className="flex flex-col gap-1">
					<h2 className="text-base font-semibold text-zinc-900">
						Open a new ticket
					</h2>
					<p className={HINT}>
						Pick a priority. Your reference stays idempotent — re-submitting
						the same <code className="font-mono">client_ticket_id</code> will
						not create a duplicate.
					</p>
				</div>
			</div>
			<form
				onSubmit={props.onSubmit}
				data-testid="create-ticket-form"
				className="mt-4 flex flex-col gap-4"
				noValidate
			>
				<div className="grid gap-3 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="tk-ref" className={LABEL}>
							Your reference
						</label>
						<input
							id="tk-ref"
							name="client_ticket_id"
							data-testid="input-client-ticket-id"
							type="text"
							className={INPUT}
							placeholder="inc-2026-04-21-001"
							value={props.draft.client_ticket_id}
							disabled={props.submitting}
							onChange={(e) =>
								props.onChange({ client_ticket_id: e.currentTarget.value })
							}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="tk-priority" className={LABEL}>
							Priority
						</label>
						<select
							id="tk-priority"
							name="priority"
							data-testid="input-priority"
							className={SELECT}
							value={props.draft.priority}
							disabled={props.submitting}
							onChange={(e) =>
								props.onChange({
									priority: e.currentTarget.value as TicketPriority,
								})
							}
						>
							{PRIORITY_ORDER.map((p) => (
								<option key={p} value={p}>
									{priorityLabel(p)}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="tk-subject" className={LABEL}>
						Subject
					</label>
					<input
						id="tk-subject"
						name="subject"
						data-testid="input-subject"
						type="text"
						className={INPUT}
						placeholder="Ingestion worker dropping events"
						value={props.draft.subject}
						disabled={props.submitting}
						onChange={(e) =>
							props.onChange({ subject: e.currentTarget.value })
						}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="tk-body" className={LABEL}>
						Details
					</label>
					<textarea
						id="tk-body"
						name="body"
						data-testid="input-body"
						rows={4}
						className={TEXTAREA}
						placeholder="Timestamps, IDs, and anything helpful for triage."
						value={props.draft.body}
						disabled={props.submitting}
						onChange={(e) => props.onChange({ body: e.currentTarget.value })}
					/>
				</div>
				{props.error ? (
					<p
						className={ERROR_TEXT}
						role="alert"
						data-testid="create-ticket-error"
					>
						{props.error}
					</p>
				) : null}
				<div className="flex items-center justify-end gap-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						data-testid="create-ticket-submit"
						disabled={props.submitting}
					>
						<IconPlus className="h-4 w-4" />
						{props.submitting ? "Filing…" : "File ticket"}
					</button>
				</div>
			</form>
		</section>
	);
}
